//////////////////////////////////////////////////////////////////////////////
//
//  에러 핸들러
//
//  작성자: MyungHwan Kim
//  생성일: 2025-10-24
//
//  설명: 네트워크 에러 처리 및 사용자 알림 (React Web용)
//
//////////////////////////////////////////////////////////////////////////////

import { ErrorAlertType, ERROR_ALERT_TYPE } from '../constants/ApiConstants';

// Alert 중복 표시 방지를 위한 상태 관리
let activeAlertType: ErrorAlertType | null = null;
let alertTimeout: ReturnType<typeof setTimeout> | null = null;

/**
 * 중앙화된 에러 알림 메서드 (React Web용)
 * @param errorType - 에러 타입
 * @param onConfirm - 확인 버튼 클릭 시 실행할 콜백 (선택)
 */
export const showErrorAlert = (
  errorType: ErrorAlertType,
  onConfirm?: () => void,
) => {
  // 이미 같은 타입의 Alert가 표시 중이면 무시
  if (activeAlertType === errorType) {
    console.log(`⚠️ Alert already showing for ${errorType}, skipping...`);
    return;
  }

  // 이전 타임아웃 클리어
  if (alertTimeout) {
    clearTimeout(alertTimeout);
  }

  // 현재 Alert 타입 설정
  activeAlertType = errorType;

  try {
    let title = '오류';
    let message = '예상치 못한 문제가 발생했어요. 잠시 후 다시 시도해주세요.';

    switch (errorType) {
      case ERROR_ALERT_TYPE.TIMEOUT:
        title = '요청 시간 초과';
        message = '서버 응답이 지연되고 있어요. 잠시 후 다시 시도해주세요.';
        break;

      case ERROR_ALERT_TYPE.SERVER_ERROR:
        title = '서버 점검 중';
        message = '서버에 일시적인 문제가 발생했어요. 잠시 후 다시 시도해주세요.';
        break;

      case ERROR_ALERT_TYPE.NETWORK_ERROR:
        title = '네트워크 연결 오류';
        message = '인터넷 연결 상태를 확인해주세요.';
        break;

      case ERROR_ALERT_TYPE.SESSION_EXPIRED:
        title = '로그인 세션 만료';
        message = '보안을 위해 로그인 세션이 만료되었어요. 다시 로그인해주세요.';
        break;

      case ERROR_ALERT_TYPE.HTTP_ERROR:
        title = '인증 오류';
        message = '로그인 정보 확인 중 문제가 발생했어요. 다시 시도해주세요.';
        break;

      default:
        title = '오류';
        message = '예상치 못한 문제가 발생했어요. 잠시 후 다시 시도해주세요.';
        break;
    }

    // React Web용 Alert (window.alert 사용)
    window.alert(`${title}\n\n${message}`);

    // Alert 상태 리셋
    activeAlertType = null;
    if (alertTimeout) {
      clearTimeout(alertTimeout);
      alertTimeout = null;
    }

    if (onConfirm) {
      onConfirm();
    }

    // 5초 후 자동으로 상태 리셋 (사용자가 Alert를 닫지 않을 경우 대비)
    alertTimeout = setTimeout(() => {
      activeAlertType = null;
      alertTimeout = null;
    }, 5000);
  } catch (alertError) {
    console.error(`🚨 Error showing ${errorType} alert:`, alertError);

    // Alert 상태 리셋
    activeAlertType = null;
    if (alertTimeout) {
      clearTimeout(alertTimeout);
      alertTimeout = null;
    }

    // 폴백 알림
    window.alert('오류\n\n알림 표시 중 문제가 발생했습니다.');
  }
};

